import API_BASE_URL from '../../config/api';
import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Button from '../../components/base/Button';
import StructuredDataViewer from '../../components/base/StructuredDataViewer';
import { AuthContext } from '../../context/AuthContext';

export default function Playground() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const [username, setUsername] = useState<string | null>(auth?.username || localStorage.getItem('username'));
  const [selectedParser, setSelectedParser] = useState('');
  const [parsers, setParsers] = useState<string[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<Record<string, any>>({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [intelligenceLevel, setIntelligenceLevel] = useState<'Standard' | 'Advanced' | ''>('Advanced');
  const [companyId, setCompanyId] = useState<string>('');
  const [selectedParserCompanyId, setSelectedParserCompanyId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isSendingResponse, setIsSendingResponse] = useState(false);

  useEffect(() => {
    const loadUsername = async () => {
      if (auth?.username) {
        setUsername(auth.username);
        return;
      }
      try {
        const resp = await fetch(`${API_BASE_URL}/user`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const data = await resp.json();
        if (data.success && data.username) {
          setUsername(data.username);
        }
        // Check if user is admin
        const userRole = localStorage.getItem('userRole');
        setIsAdmin(userRole === 'admin' || userRole === 'super_admin');
      } catch (e) {
        console.error('Unable to fetch user', e);
      }
    };
    loadUsername();
    // Also check localStorage for admin role
    const role = localStorage.getItem('userRole') || 'user';
    setIsAdmin(role === 'admin' || role === 'super_admin');
  }, [auth]);

  useEffect(() => {
    const loadCompanyId = async () => {
      try {
        // Extract company_id from token instead of searching all users
        // Token format: token-username-company_id
        const token = localStorage.getItem('token') || '';
        if (token && token.startsWith('token-')) {
          const tokenParts = token.substring(6).split('-'); // Remove 'token-' prefix and split
          if (tokenParts.length >= 2) {
            // Company ID is everything after the username
            const extractedCompanyId = tokenParts.slice(1).join('-'); // Join in case company_id contains hyphens
            setCompanyId(extractedCompanyId);
            return;
          }
        }
        
        // Fallback: try to get from API if token doesn't have company_id
        const currentUsername = auth?.username || localStorage.getItem('username');
        if (currentUsername) {
          const response = await fetch(`${API_BASE_URL}/user`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          const data = await response.json();
          if (data.success && data.company_id) {
            setCompanyId(data.company_id);
          }
        }
      } catch (error) {
        console.error('Failed to load company ID:', error);
      }
    };
    loadCompanyId();
  }, [auth]);

  useEffect(() => {
    const fetchParsers = async () => {
      try {
        // Backend now returns parsers specific to the authenticated user
        // No need to pass username in query params since backend uses token
        const url = `${API_BASE_URL}/list-parsers`;
        const resp = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          },
        });
        const data = await resp.json();
        
        if (data.success && Array.isArray(data.parsers)) {
          // Backend already filters by user, so just use all returned parsers
          // For regular users: returns just filenames
          // For admin/super_admin: returns company_id/filename paths
          const filteredParsers = data.parsers.filter((p: string) => p.endsWith('.json'));
          console.log(`[PLAYGROUND] Loaded parsers from backend:`, filteredParsers);
          setParsers(filteredParsers);
        }
      } catch (e) {
        console.error('Unable to load parsers', e);
      }
    };
    
    // initial fetch
    fetchParsers();
    // microtask fetch to catch just-written tokens after redirects
    const t = setTimeout(fetchParsers, 0);
    // refetch on window focus and cross-tab storage token updates
    const onFocus = () => fetchParsers();
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'token') fetchParsers();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      clearTimeout(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, [username]);

  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
    setExtractedData({}); // Reset extracted data
    setError(null); // Reset error
    try {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } catch (e) {
      console.error('Preview URL error', e);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleExtract = async () => {
    if (isExtracting) return;
    if (!selectedParser || !uploadedFile) {
      setError('Please select a parser and upload a file');
      return;
    }

    setIsExtracting(true);
    setError(null);

    // Determine model type: "pro" for Standard Model, "flash" for Advanced Model, default to "pro"
    const modelType = intelligenceLevel === 'Advanced' ? 'flash' : 'pro';

    const formData = new FormData();
    formData.append('file', uploadedFile);
    // For admin: parser_name already includes company_id prefix from backend (e.g., "eri00784/filename.json")
    // For regular users: add logged-in user's company_id prefix
    // For admin/super_admin: use the parser name as-is (it already includes company_id from dropdown)
    // CRITICAL: Use selectedParser exactly as selected - do not modify the company_id part
    let parserNameToSend = selectedParser;
    console.log(`[PLAYGROUND] handleExtract START - selectedParser: "${selectedParser}", isAdmin: ${isAdmin}, selectedParserCompanyId: "${selectedParserCompanyId}", companyId: "${companyId}"`);
    
    if (!parserNameToSend) {
      setError('Please select a parser');
      return;
    }
    
    if (parserNameToSend.includes('/')) {
      // Parser name includes company_id prefix (e.g., "dv002/rashid_exp.json")
      // For admin/super_admin: use it exactly as-is, just normalize to lowercase
      // DO NOT use selectedParserCompanyId or companyId - use the company_id from parser name itself
      const parts = parserNameToSend.split('/');
      if (parts.length >= 2) {
        // Extract company_id from the parser name itself
        const parserCompanyId = parts[0].toLowerCase();
        const parserFilename = parts.slice(1).join('/');
        parserNameToSend = `${parserCompanyId}/${parserFilename}`;
        console.log(`[PLAYGROUND] Parser includes company_id - using company_id from parser: "${parserCompanyId}", filename: "${parserFilename}"`);
        console.log(`[PLAYGROUND] Final normalized parser name: "${parserNameToSend}"`);
      }
    } else {
      // Parser name doesn't include company_id prefix (shouldn't happen for admin, but handle it)
      // Use selectedParserCompanyId (extracted from parser) or companyId (from token)
      const companyIdToUse = selectedParserCompanyId || companyId;
      if (companyIdToUse) {
        parserNameToSend = `${companyIdToUse.toLowerCase()}/${parserNameToSend}`;
        console.log(`[PLAYGROUND] Added company_id prefix: "${parserNameToSend}"`);
      } else {
        console.warn('[PLAYGROUND] No company_id available to add to parser name');
        setError('Unable to determine company ID for parser');
        return;
      }
    }
    
    console.log(`[PLAYGROUND] FINAL parser_name being sent to backend: "${parserNameToSend}" (isAdmin: ${isAdmin})`);
    formData.append('parser_name', parserNameToSend);
    formData.append('username', username || '');
    formData.append('model_type', modelType);

    try {
      console.log('Sending request to /process-document');
      const response = await fetch(`${API_BASE_URL}/process-document`, {
        method: 'POST',
        body: formData,
      });
      console.log('Response received', response);
      const result = await response.json();
      if (result.success) {
        // Normalize payload from backend:
        // - Sometimes returns plain object
        // - Sometimes returns { status, details, page_count, data }
        // - Sometimes returns a JSON string
        let payload: any = result.extracted_data || {};
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch (_) {
            // keep as string
          }
        }
        if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
          payload = payload.data;
        }
        setExtractedData(payload || {});
        setError(null);
      } else {
        setError(result.error || 'Extraction failed');
      }
    } catch (error) {
      console.error('Network error:', error);
      setError('Network error: ' + (error as Error).message);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!selectedParser || !intelligenceLevel) {
      setError('Please select a parser and intelligence level');
      return;
    }

    setIsSaving(true);
    setError(null);

    // Determine model type: "pro" for Standard Model, "flash" for Advanced Model
    const modelType = intelligenceLevel === 'Advanced' ? 'flash' : 'pro';

    try {
      const response = await fetch(`${API_BASE_URL}/save-generation-function`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          parser_name: selectedParser,
          model_type: modelType,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setError(null);
        alert('Generation function saved successfully!');
      } else {
        setError(result.error || 'Failed to save generation function');
      }
    } catch (error) {
      console.error('Network error:', error);
      setError('Network error: ' + (error as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendResponse = async () => {
    if (isSendingResponse) return;
    if (!selectedParser || !uploadedFile) {
      setError('Please select a parser and upload a file');
      return;
    }

    setIsSendingResponse(true);
    setError(null);

    try {
      // Extract pdfclientname and jobtype from selectedParser
      // Parser format: "company_id/pdfclientname_jobtype.json" or "pdfclientname_jobtype.json"
      let parserFilename = selectedParser;
      if (selectedParser.includes('/')) {
        const parts = selectedParser.split('/');
        parserFilename = parts[parts.length - 1] || selectedParser;
      }

      // Remove .json extension
      const parserBase = parserFilename.replace(/\.json$/i, '');
      const parserParts = parserBase.split('_');

      let pdfClientName = '';
      let jobType = '';

      if (parserParts.length >= 2) {
        // Standard format: pdfclientname_jobtype
        pdfClientName = parserParts.slice(0, -1).join('_').toUpperCase(); // Everything except last part
        jobType = parserParts[parserParts.length - 1].toUpperCase(); // Last part is jobtype (EXP/IMP)
      } else if (parserParts.length === 1) {
        // Fallback: if no underscore, use the whole name as pdfclientname
        pdfClientName = parserParts[0].toUpperCase();
        jobType = 'N/A';
      }

      // Use full file name with extension
      const fileName = uploadedFile.name;

      // Log extracted values
      console.log('[PLAYGROUND] Send Response - Extracted values:');
      console.log('  - CompanyID:', companyId?.toUpperCase() || '');
      console.log('  - UserID:', username || '');
      console.log('  - Type (jobtype):', jobType);
      console.log('  - FileName:', fileName);
      console.log('  - PdfClientName:', pdfClientName);
      console.log('  - Selected Parser:', selectedParser);

      const requestBody = {
        ResponseData: [
          {
            CompanyID: (companyId || '').toUpperCase(),
            UserID: username || '',
            Type: jobType,
            FileName: fileName,
            PdfClientName: pdfClientName,
          },
        ],
      };

      // Log the JSON payload in a formatted way
      console.log('[PLAYGROUND] Send Response - JSON Payload (object):', requestBody);
      console.log('[PLAYGROUND] Send Response - JSON Payload (stringified):', JSON.stringify(requestBody, null, 2));
      
      // Use backend proxy endpoint to avoid CORS issues
      const proxyUrl = `${API_BASE_URL}/api/ocr/responses`;
      console.log('[PLAYGROUND] Send Response - API URL (via backend proxy):', proxyUrl);

      // Prepare request headers - backend proxy will add ApiKey header
      const requestHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`, // Include auth token for backend
      };
      console.log('[PLAYGROUND] Send Response - Request Headers:', requestHeaders);

      const requestStartTime = Date.now();
      console.log('[PLAYGROUND] Send Response - Sending POST request via backend proxy...');

      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(requestBody),
      });

      const requestDuration = Date.now() - requestStartTime;
      console.log(`[PLAYGROUND] Send Response - Request completed in ${requestDuration}ms`);
      console.log('[PLAYGROUND] Send Response - Response Status:', response.status, response.statusText);
      console.log('[PLAYGROUND] Send Response - Response Headers:', Object.fromEntries(response.headers.entries()));

      const result = await response.json().catch((parseError) => {
        console.error('[PLAYGROUND] Send Response - Failed to parse JSON response:', parseError);
        return null;
      });

      if (response.ok) {
        setError(null);
        console.log('[PLAYGROUND] Send Response - SUCCESS: OCR API response received:', result);
        console.log('[PLAYGROUND] Send Response - Response data:', JSON.stringify(result, null, 2));
        alert('Response sent successfully!');
      } else {
        console.error('[PLAYGROUND] Send Response - ERROR: Request failed');
        console.error('[PLAYGROUND] Send Response - Status Code:', response.status);
        console.error('[PLAYGROUND] Send Response - Status Text:', response.statusText);
        console.error('[PLAYGROUND] Send Response - Error Response:', result);
        setError(
          (result && (result.error || result.message)) ||
            `Failed to send response: ${response.status} ${response.statusText}`,
        );
      }
    } catch (err) {
      console.error('Network error while sending OCR response:', err);
      setError('Network error while sending OCR response');
    } finally {
      setIsSendingResponse(false);
    }
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'ri-file-pdf-line';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'ri-image-line';
      case 'doc':
      case 'docx':
        return 'ri-file-word-line';
      default:
        return 'ri-file-line';
    }
  };

  // Display full composite name as companyId/parserName
  // Only add company ID if it's not already in the path
  const getParserDisplayName = (fullPath: string) => {
    // If parser name already includes a path (company_id/filename.json), use it as-is
    if (fullPath.includes('/')) {
      return fullPath;
    }
    // Otherwise, add the current user's company ID
    const filename = fullPath;
    // Use companyId from state (extracted from token) to ensure correct company ID
    return companyId ? `${companyId.toUpperCase()}/${filename}` : filename;
  };

  
  // Extract company ID from parser name when selected
  useEffect(() => {
    if (selectedParser) {
      if (isAdmin && selectedParser.includes('/')) {
        // Admin: extract company_id from parser name (format: "company_id/filename.json")
        const parts = selectedParser.split('/');
        if (parts.length >= 2) {
          setSelectedParserCompanyId(parts[0].toUpperCase());
        } else {
          setSelectedParserCompanyId(companyId.toUpperCase() || '');
        }
      } else {
        // Regular user: use logged-in user's company_id
        setSelectedParserCompanyId(companyId.toUpperCase() || '');
      }
    } else {
      setSelectedParserCompanyId('');
    }
  }, [selectedParser, companyId, isAdmin]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <Navbar showUserActions={true} />
      
      <div className="px-4 py-6">
        <Button variant="outline" onClick={() => navigate('/home')} className="mb-6">
          Back
        </Button>
      </div>
      
      <div className="flex flex-col items-center px-6">
        <div className="w-full max-w-8xl">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-800 mb-4">Playground</h1>
            <div className="w-full h-px bg-gray-300"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Side */}
            <div className="space-y-6">
              {/* Parser Dropdown */}
              <div>
                <label className="block text-sm font-semibold text-indigo-600 mb-2">
                  Select Parser
                </label>
                <div className="relative">
                  <select
                    value={selectedParser}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      console.log(`[PLAYGROUND] Parser selected: ${newValue} (from dropdown)`);
                      setSelectedParser(newValue);
                    }}
                    className="w-full px-5 py-4 border-2 border-cyan-300 rounded-lg focus:outline-none focus:border-cyan-500 bg-white pr-10 text-base"
                  >
                    <option value="">Choose a parser...</option>
                    {parsers.map((fullPath) => {
                      // For admin/super_admin users: preserve company_id prefix from backend (e.g., "dv002/filename.json")
                      // For regular users: extract just filename (backend returns just filename for their company)
                      let parserValue = fullPath;
                      if (!isAdmin && fullPath.includes('/')) {
                        // Regular user: backend might return path, extract just filename
                        const parts = fullPath.split('/');
                        parserValue = parts.slice(-1)[0]; // Get last part (filename)
                      }
                      // Admin/Super Admin: keep full path with company_id prefix as returned by backend
                      return (
                        <option key={fullPath} value={parserValue}>
                          {getParserDisplayName(fullPath)}
                        </option>
                      );
                    })}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </div>
                </div>
                {selectedParserCompanyId && (
                  <div className="mt-2 text-sm text-gray-600">
                    <span className="font-semibold">Company ID:</span> {selectedParserCompanyId}
                  </div>
                )}
              </div>

              {/* Model Tier Selection */}
              <div>
                <label className="block text-sm font-semibold text-indigo-600 mb-2">
                  Model Tier
                </label>
                <div className="flex items-center gap-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modelTier"
                      value="Standard"
                      checked={intelligenceLevel === 'Standard'}
                      onChange={(e) => setIntelligenceLevel(e.target.value as 'Standard' | 'Advanced')}
                      className="w-5 h-5 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-gray-700">Standard</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modelTier"
                      value="Advanced"
                      checked={intelligenceLevel === 'Advanced'}
                      onChange={(e) => setIntelligenceLevel(e.target.value as 'Standard' | 'Advanced')}
                      className="w-5 h-5 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-gray-700">Advanced</span>
                  </label>
                </div>
                {/* Save Button under Intelligence Level */}
                <div className="mt-4">
                  <Button
                    onClick={handleSave}
                    disabled={!selectedParser || !intelligenceLevel || isSaving}
                    variant="outline"
                    className={`w-full px-6 py-2 text-base whitespace-nowrap ${(!selectedParser || !intelligenceLevel || isSaving) ? 'opacity-50 cursor-not-alStandarded pointer-events-none' : ''}`}
                  >
                    {isSaving ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line mr-2"></i>
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </div>


              {/* Document Preview */}
              <div>
                <label className="block text-sm font-semibold text-indigo-600 mb-2">
                  Document Preview
                </label>
                <div className="bg-white border-2 border-gray-200 rounded-lg p-6 min-h-48">
                  {uploadedFile ? (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-lg">
                          <i className={`${getFileIcon(uploadedFile.name)} text-2xl text-gray-600`}></i>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{uploadedFile.name}</p>
                          <p className="text-sm text-gray-500">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      {previewUrl && uploadedFile.type === 'application/pdf' && (
                        <iframe src={previewUrl} className="w-full h-[80vh] max-h-[1000px] border rounded" title="PDF preview"></iframe>
                      )}
                      {previewUrl && uploadedFile.type.startsWith('image/') && (
                        <img src={previewUrl} alt="preview" className="max-h-[80vh] max-w-full rounded" />
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500">
                      <i className="ri-file-line text-4xl mb-2"></i>
                      <p>No document uploaded</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side */}
            <div className="space-y-6">
              {/* Drag and Drop Upload */}
              <div>
                <label className="block text-sm font-semibold text-indigo-600 mb-2">
                  Upload Document
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragOver 
                      ? 'border-cyan-400 bg-cyan-50' 
                      : 'border-gray-300 hover:border-cyan-300'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-600 mb-4">
                    Drag and drop your document here, or click to browse
                  </p>
                  <input
                    type="file"
                    onChange={handleFileInputChange}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                  <label
                    htmlFor="file-upload"
                    className="inline-block px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    Browse Files
                  </label>
                </div>
              </div>

              {/* Extracted Data Viewer (scrollable) */}
              <div className="bg-white border-2 border-gray-200 rounded-lg p-8 max-h-[70vh] overflow-auto">
                <StructuredDataViewer data={extractedData} />
                {error && <p className="text-red-500 mt-2">{error}</p>}
              </div>

              {/* Extract & Send Response Buttons */}
              <div className="flex justify-center gap-4">
                <Button
                  onClick={handleExtract}
                  disabled={!selectedParser || !uploadedFile || isExtracting}
                  className={`px-8 py-3 text-lg whitespace-nowrap ${
                    !selectedParser || !uploadedFile || isExtracting
                      ? 'opacity-50 cursor-not-allowed pointer-events-none'
                      : ''
                  }`}
                >
                  {isExtracting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Extracting...
                    </>
                  ) : (
                    <>
                      <i className="ri-extract-line mr-2"></i>
                      Extract
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleSendResponse}
                  disabled={!selectedParser || !uploadedFile || isSendingResponse}
                  variant="outline"
                  className={`px-8 py-3 text-lg whitespace-nowrap ${
                    !selectedParser || !uploadedFile || isSendingResponse
                      ? 'opacity-50 cursor-not-allowed pointer-events-none'
                      : ''
                  }`}
                >
                  {isSendingResponse ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Send Response
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
